import type { TFunction } from 'i18next'
import * as z from 'zod'

export const CARD_BRANDS = [
    'visa',
    'mastercard',
    'american_express',
    'discover',
    'diners_club',
    'jcb',
    'unionpay',
] as const

export type CardBrand = (typeof CARD_BRANDS)[number]

export const CARD_BRAND_LABELS: Record<CardBrand, string> = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    american_express: 'Amex',
    discover: 'Discover',
    diners_club: 'Diners',
    jcb: 'JCB',
    unionpay: 'UnionPay',
}

/** 6–8 digits, or a 6-digit range like 440000-449999. */
export const ROUTING_BIN_PATTERN = /^(?:\d{6,8}|\d{6}-\d{6})$/

/**
 * A processor card stacks three things that decide different questions: its share of the traffic,
 * the brands it must never see, and the conditions that override the share. They share one header
 * and one caption style so each group reads as a block with a visible start.
 */
export const GROUP_LABEL_CLASSES = 'text-[10px] font-semibold uppercase tracking-wider text-muted-foreground'
export const GROUP_CAPTION_CLASSES = 'text-[10px] leading-snug text-muted-foreground'

const ROUTING_CURRENCY_PATTERN = /^[A-Za-z]{3}$/

export const pinAmountCurrencies = (supported?: string[] | null): string[] => {
    const unique = new Set<string>()
    for (const code of supported ?? []) {
        const upper = code.toUpperCase()
        if (ROUTING_CURRENCY_PATTERN.test(upper)) {
            unique.add(upper)
        }
    }
    return [...unique]
}

export const normalizePinCurrency = (currency: string | undefined): string | undefined => {
    if (!currency) {
        return undefined
    }
    const upper = currency.trim().toUpperCase()
    return ROUTING_CURRENCY_PATTERN.test(upper) ? upper : undefined
}

export const createRoutingSchema = (t: TFunction) => {
    const amountRangeSchema = z
        .object({
            min: z.number().min(0).optional(),
            max: z.number().min(0).optional(),
            currency: z
                .string()
                .optional()
                .refine((value) => !value || ROUTING_CURRENCY_PATTERN.test(value), {
                    message: t('paymentFlows:validation.routingCurrency'),
                }),
        })
        .refine(
            (range) => range.min === undefined || range.max === undefined || range.min < range.max,
            { message: t('paymentFlows:validation.routingAmountRange') },
        )

    const pinSchema = z.object({
        amount: z.array(amountRangeSchema).optional(),
        brands: z.array(z.enum(CARD_BRANDS)).optional(),
        bins: z
            .array(z.string().regex(ROUTING_BIN_PATTERN, t('paymentFlows:validation.routingBin')))
            .optional(),
    })

    return z.object({
        pins: z.array(pinSchema).optional(),
    })
}

export type RoutingFormValue = NonNullable<z.infer<ReturnType<typeof createRoutingSchema>>>
export type RoutingPinFormValue = NonNullable<RoutingFormValue['pins']>[number]

export const emptyRoutingPin = (): RoutingPinFormValue => ({
    amount: [{}],
    brands: [],
    bins: [],
})

export const routingFromConfig = (config: unknown): RoutingFormValue =>
    (config as { routing?: RoutingFormValue }).routing ?? { pins: [] }

const hasAmountBound = (range: { min?: number; max?: number }): boolean =>
    range.min !== undefined || range.max !== undefined

export const pinHasConditions = (pin: RoutingPinFormValue): boolean => {
    const hasAmount = (pin.amount ?? []).some(hasAmountBound)
    const hasBrands = (pin.brands ?? []).length > 0
    const hasBins = (pin.bins ?? []).length > 0
    return hasAmount || hasBrands || hasBins
}

export const routingForSubmit = (
    routing: RoutingFormValue | undefined,
): RoutingFormValue | undefined => {
    const pins = (routing?.pins ?? [])
        .map((pin) => {
            const amount = (pin.amount ?? [])
                .filter(hasAmountBound)
                .map((range) => {
                    const currency = normalizePinCurrency(range.currency)
                    return {
                        ...(range.min !== undefined ? { min: range.min } : {}),
                        ...(range.max !== undefined ? { max: range.max } : {}),
                        ...(currency ? { currency } : {}),
                    }
                })
            const brands = (pin.brands ?? []).length > 0 ? pin.brands : undefined
            const bins = (pin.bins ?? []).length > 0 ? pin.bins : undefined
            return {
                ...(amount.length > 0 ? { amount } : {}),
                ...(brands ? { brands } : {}),
                ...(bins ? { bins } : {}),
            }
        })
        .filter(pinHasConditions)

    return pins.length > 0 ? { pins } : undefined
}

export const parseBinEntries = (raw: string): { entries: string[]; invalid: string[] } => {
    const entries = raw
        .split(/[\n,]+/)
        .map((token) => token.trim())
        .filter(Boolean)
    const invalid = entries.filter((entry) => !ROUTING_BIN_PATTERN.test(entry))
    return { entries, invalid }
}

export const isRoutingBrandDisabled = (
    brand: string,
    disabledCardBrands: string[] | undefined,
): boolean => (disabledCardBrands ?? []).includes(brand)

export const toggleRoutingBrand = (
    brands: CardBrand[] | undefined,
    brand: CardBrand,
    disabledCardBrands: string[] | undefined,
): CardBrand[] => {
    const current = brands ?? []
    if (isRoutingBrandDisabled(brand, disabledCardBrands)) {
        return current
    }
    if (current.includes(brand)) {
        return current.filter((item) => item !== brand)
    }
    return [...current, brand]
}

export const formatPayfacProviderLabel = (provider: string): string =>
    provider.charAt(0).toUpperCase() + provider.slice(1)

const describeAmountRange = (
    range: { min?: number; max?: number; currency?: string },
    t: TFunction,
): string | null => {
    const { min, max } = range
    const text =
        min !== undefined && max !== undefined
            ? t('paymentFlows:form.fields.routing.summary_amount_range', { min, max })
            : min !== undefined
              ? t('paymentFlows:form.fields.routing.summary_amount_min', { min })
              : max !== undefined
                ? t('paymentFlows:form.fields.routing.summary_amount_max', { max })
                : null
    if (!text) {
        return null
    }
    const currency = normalizePinCurrency(range.currency)
    return currency ? `${text} ${currency}` : text
}

/**
 * One condition rendered as its ANDed parts, for the read-only recap on the flow detail pages.
 * Empty array means the condition sets nothing and would never match.
 */
export const describeRoutingPin = (pin: RoutingPinFormValue, t: TFunction): string[] => {
    const parts: string[] = []

    const amounts = (pin.amount ?? [])
        .map((range) => describeAmountRange(range, t))
        .filter((text): text is string => text !== null)
    if (amounts.length > 0) {
        parts.push(amounts.join(' / '))
    }

    const brands = pin.brands ?? []
    if (brands.length > 0) {
        parts.push(brands.map((brand) => CARD_BRAND_LABELS[brand] ?? brand).join('/'))
    }

    const bins = pin.bins ?? []
    if (bins.length === 1) {
        parts.push(t('paymentFlows:form.fields.routing.summary_bin', { list: bins[0] }))
    } else if (bins.length > 1) {
        parts.push(t('paymentFlows:form.fields.routing.summary_bin_count', { total: bins.length }))
    }

    return parts
}
